import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import PrimaryButton from "../../Components/Buttons/primary-button";
import OutlineButton from "../../Components/Buttons/outline-button";

import { LocalStorage } from "../../Utils/helpers";
import { appUtilityKeys } from "../../Utils/constants";
import { APIhandler } from "../../Utils/api";
import { StakeLogo } from "../../Assets/Images";
import { useTranslation } from "react-i18next";
import ChatEngine from "./flow/ChatEngine";
import { WELCOME_FLOW_SCHEMA } from "./flow/schema";

import "./stake-ai-chat.scss";

const RESPONSES_STORAGE_KEY = "stake_ai_find_home_responses";

function useTypewriter(text = "", speed = 9) {
  const [display, setDisplay] = useState("");
  useEffect(() => {
    setDisplay("");
    let idx = 0;
    let timer = null;
    const tick = () => {
      setDisplay(text.slice(0, idx + 1));
      idx += 1;
      if (idx < text.length) {
        timer = setTimeout(tick, speed);
      }
    };
    timer = setTimeout(tick, speed);
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [text, speed]);
  return display;
}

function TypeText({ text }) {
  const [done, setDone] = useState(false);
  const typed = useTypewriter(done ? "" : text);
  useEffect(() => {
    if (!done && typed === text) {
      setDone(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typed, text]);
  return <p className="typewriter">{done ? text : typed}</p>;
}

function ChatBubble({ role = "system", children, delayed = 0 }) {
  const ref = useRef(null);
  useEffect(() => {
    const t = setTimeout(() => {
      if (ref.current) ref.current.classList.add("visible");
    }, delayed);
    return () => clearTimeout(t);
  }, [delayed]);
  return (
    <div ref={ref} className={`stake-ai-bubble ${role} fade-in`}>
      {role === "system" && (
        <div className="avatar">
          <img src="/favicon-32x32.png" alt="Stake" />
        </div>
      )}
      <div className="bubble-inner">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="stake-ai-field">
      <label className="text-sm-medium color--mist">{label}</label>
      {children}
    </div>
  );
}

export default function StakeAIChat({ onComplete }) {
  const navigate = useNavigate();

  // Noindex for this page only
  useEffect(() => {
    let meta = document.querySelector('meta[name="robots"]');
    const previous = meta ? meta.getAttribute("content") : null;
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "robots");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", "noindex,nofollow");
    return () => {
      if (previous === null) {
        document.head.removeChild(meta);
      } else {
        meta.setAttribute("content", previous);
      }
    };
  }, []);

  // Load Osano privacy compliance script
  useEffect(() => {
    // Check if script already exists to avoid duplicate loading
    if (document.querySelector('script[src*="osano.com"]')) {
      return;
    }
    
    const script = document.createElement("script");
    script.src = "https://cmp.osano.com/169lKBSp2p8CG2r40/5ef487f8-ca35-466a-ae5b-21b1f5bcebb4/osano.js";
    script.async = true;
    document.head.appendChild(script);

    return () => {
      // Cleanup: remove script on unmount if needed
      const existingScript = document.querySelector('script[src*="osano.com"]');
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, []);

  const knownUser = !!LocalStorage.read(appUtilityKeys.loggedInUserData);
  const { t, i18n } = useTranslation("welcome");
  const [step, setStep] = useState(0);
  const [responses, setResponses] = useState(() => LocalStorage.read(RESPONSES_STORAGE_KEY) || {});
  const [renterReasons, setRenterReasons] = useState([]);
  const [otherReason, setOtherReason] = useState("");
  const [showGoogleCTA, setShowGoogleCTA] = useState(false);
  const [personaEcho, setPersonaEcho] = useState(null);
  const [reasonEcho, setReasonEcho] = useState(null);
  const [manualMode, setManualMode] = useState(false);
  const [manualName, setManualName] = useState("");
  const [manualEmail, setManualEmail] = useState("");
  const [manualPhone, setManualPhone] = useState("");
  const [rentPayMethod, setRentPayMethod] = useState(null);
  const [rentPayOther, setRentPayOther] = useState("");

  // Track Google OAuth in-flight state across renders (avoids double triggers in StrictMode)
  const googleSignInInFlightRef = useRef(false);

  const save = (patch) => {
    const next = { ...responses, ...patch, savedAt: new Date().toISOString() };
    setResponses(next);
    LocalStorage.write(RESPONSES_STORAGE_KEY, next);
  };

  const getReasonLabel = (key) => {
    switch (key) {
      case "cashback":
        return t("reasons.cashback");
      case "credit":
        return t("reasons.credit");
      case "find-home":
        return t("reasons.findHome");
      case "find-home-cashback":
        return t("reasons.findHome");
      case "other":
        return `${t("reasons.other")}${otherReason?.trim() ? `: ${otherReason.trim()}` : ""}`;
      default:
        return "";
    }
  };

  const getTailoredIntro = (reasons) => {
    if (reasons.includes("find-home") || reasons.includes("find-home-cashback")) {
      return t("intro.findHome");
    }
    if (reasons.includes("cashback")) {
      return t("intro.cashback");
    }
    if (reasons.includes("credit")) {
      return t("intro.credit");
    }
    return t("intro.generic");
  };

  const greet = useMemo(() => (knownUser ? t("greetKnown") : t("greetNew")), [knownUser, t]);
  const typedGreet = useTypewriter(greet);

  const onSelectPersona = (persona) => {
    save({ persona });
    if (persona === "renter") {
      setStep(1);
      setShowGoogleCTA(false);
      setPersonaEcho(t("persona.renter"));
    } else if (persona === "agent") {
      setStep(100); // show Google sign-in immediately
      setShowGoogleCTA(true);
      setPersonaEcho(t("persona.agent"));
    } else {
      setStep(200); // property manager/owner
      setShowGoogleCTA(true);
      setPersonaEcho(t("persona.owner"));
    }
  };

  const reasonsIncludes = (val) => renterReasons.includes(val);
  const toggleReason = (val) =>
    setRenterReasons((prev) => {
      const exists = prev.includes(val);
      const next = exists ? prev.filter((v) => v !== val) : [...prev, val];
      setShowGoogleCTA(next.length > 0);
      save({ renterReasons: next });
      if (!reasonEcho && next.length > 0) {
        setReasonEcho(getReasonLabel(val));
      }
      return next;
    });

  const reasonsWithOther = () =>
    otherReason?.trim() ? [...renterReasons, `other:${otherReason.trim()}`] : renterReasons;

  // Renter -> Find a home form
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    hearAboutUs: "",
    market: "",
    neighborhoods: "",
    neighborhood: "",
    moveInDate: "",
    leaseTermMonths: "",
    petsCount: "",
    petType: "",
    parkingCount: "",
    parkingPreference: "",
    sqft: "Any",
    price: "",
    bedrooms: "",
    bathrooms: "",
    notes: "",
    amenities: {
      attachedPrivateGarages: false,
      dogRun: false,
      evCharging: false,
      elevators: false,
      fencedCommunity: false,
      fencedYardsTx: false,
      petWash: false,
      rooftopDeck: false,
      wdConnections: false,
      wdIncluded: false,
    },
    background: {
      none: false,
      rentalDebt: false,
      bankruptcy: false,
      eviction: false,
      felony: false,
      misdemeanor: false,
      unpaidBrokenLease: false,
    },
    creditScore: "",
  });

  const updateForm = (key, value) => setForm((p) => ({ ...p, [key]: value }));
  const updateNested = (group, key, value) => setForm((p) => ({ ...p, [group]: { ...p[group], [key]: value } }));

  const submitFindHome = () => {
    save({ findHomeRequest: form });
    setStep(3);
    if (typeof onComplete === "function") onComplete();
  };

  const submitManualInfo = async () => {
    const softSignup = {
      persona: responses.persona,
      renterReasons: reasonsWithOther(),
      needsFindHomeForm:
        responses.persona === "renter" &&
        (renterReasons.includes("find-home") || renterReasons.includes("find-home-cashback")),
      manualContact: {
        name: manualName,
        email: manualEmail,
        phone: manualPhone,
      },
      context: { source: "welcome" },
      createdAt: new Date().toISOString(),
    };
    LocalStorage.write("stake_ai_soft_signup", softSignup);
    try {
      await APIhandler.softSignups.createSoftSignup(softSignup);
    } catch (e) {}
    if (softSignup.needsFindHomeForm) {
      setStep(2);
    } else {
      if (renterReasons.includes("cashback")) {
        setStep(4);
      } else {
        setStep(3);
      }
    }
  };

  // Google OAuth (GIS) preparation: code flow for server-side exchange
  const ensureGoogleScript = () =>
    new Promise((resolve, reject) => {
      if (window.google && window.google.accounts && window.google.accounts.oauth2) {
        resolve();
        return;
      }
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = (e) => reject(e);
      document.head.appendChild(script);
    });

  const beginGoogleSignIn = async () => {
    try {
      await ensureGoogleScript();
      const clientId = process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID;
      if (!clientId) {
        console.warn("Missing REACT_APP_GOOGLE_OAUTH_CLIENT_ID");
      }
      // Guard against duplicate invocations and duplicate callbacks
      if (googleSignInInFlightRef.current) return Promise.resolve();
      googleSignInInFlightRef.current = true;
      let handled = false;

      const fetchGoogleProfileSilently = () =>
        new Promise((resolve) => {
          try {
            const tokenClient = window.google.accounts.oauth2.initTokenClient({
              client_id: clientId,
              scope: "openid email profile",
              error_callback: () => resolve(null),
              callback: async (resp) => {
                try {
                  const r = await fetch("https://www.googleapis.com/oauth2/v3/userinfo", {
                    headers: { Authorization: `Bearer ${resp.access_token}` },
                  });
                  const data = await r.json();
                  resolve(data);
                } catch {
                  resolve(null);
                }
              },
            });
            // Attempt silent token fetch; if consent not granted, this won't show a second popup
            tokenClient.requestAccessToken({ prompt: "" });
          } catch {
            resolve(null);
          }
        });

      // Return a promise that resolves when the OAuth callback completes
      return await new Promise((resolve) => {
        const codeClient = window.google.accounts.oauth2.initCodeClient({
          client_id: clientId,
          scope: "openid email profile",
          ux_mode: "popup",
          callback: async (response) => {
            if (handled) return;
            handled = true;
            const softSignup = {
              persona: responses.persona,
              renterReasons: reasonsWithOther(),
              needsFindHomeForm:
                responses.persona === "renter" &&
                (renterReasons.includes("find-home") || renterReasons.includes("find-home-cashback")),
              googleAuth: { code: response.code },
              context: { source: "welcome" },
              createdAt: new Date().toISOString(),
            };
            // Try to fetch basic profile silently for first name UX
            const profile = await fetchGoogleProfileSilently();
            if (profile) {
              softSignup.profile = profile;
            }
            LocalStorage.write("stake_ai_soft_signup", softSignup);
            try {
              await APIhandler.softSignups.createSoftSignup(softSignup);
            } catch (e) {}

            if (softSignup.needsFindHomeForm) {
              setStep(2);
            } else {
              if (renterReasons.includes("cashback")) {
                setStep(4);
              } else {
                setStep(3);
              }
            }
            googleSignInInFlightRef.current = false;
            resolve();
          },
        });
        codeClient.requestCode();
      });
    } catch (e) {
      console.error("Google OAuth init failed", e);
      googleSignInInFlightRef.current = false;
      return Promise.resolve();
    }
  };

  const getFirstNameFromManual = () => (manualName || "").split(" ")[0] || "";
  const getFirstName = () => {
    if (manualName) return getFirstNameFromManual();
    const fromFindHome = responses?.findHomeRequest?.firstName;
    if (fromFindHome) return fromFindHome;
    const stored = LocalStorage.read("stake_ai_soft_signup");
    const manual = stored?.manualContact?.name;
    if (manual) return (manual || "").split(" ")[0] || "";
    const profGiven = stored?.profile?.given_name || stored?.profile?.givenName;
    if (profGiven) return profGiven;
    const profName = stored?.profile?.name;
    if (profName) return (profName || "").split(" ")[0] || "";
    return "";
  };

  return (
    <div className="onboarding-page stake-ai-page">
      <div className="max-width-container">
        <div className="stake-ai-chat">
          <header>
            <a href="https://getyourstake.com" style={{ textDecoration: "none", display: "inline-block" }}>
              <StakeLogo className="logo" />
            </a>
            <div className="lang-toggle">
              <button
                className={`lang-btn ${i18n.language?.startsWith('en') ? 'active' : ''}`}
                onClick={() => i18n.changeLanguage('en')}
                aria-label="Switch to English"
              >
                <span className="flag">🇺🇸 </span>
                <span className="flag">🇨🇦 </span>
                <span className="label">English</span>
              </button>
              <button
                className={`lang-btn ${i18n.language?.startsWith('es') ? 'active' : ''}`}
                onClick={() => i18n.changeLanguage('es')}
                aria-label="Cambiar a Español"
              >
                <span className="flag">🇲🇽 </span>
                <span className="flag">🇪🇸 </span>
                <span className="label">Español</span>
              </button>
            </div>
          </header>
          <div>
            <ChatEngine schema={WELCOME_FLOW_SCHEMA} onIdentityGoogle={beginGoogleSignIn} onComplete={() => {}} />
          </div>

          {false && step === 0 && (
            <div className="stake-ai-options">
              <OutlineButton onClick={() => onSelectPersona("renter")}>{t("persona.renter")}</OutlineButton>
              <OutlineButton onClick={() => onSelectPersona("agent")}>{t("persona.agent")}</OutlineButton>
              <OutlineButton onClick={() => onSelectPersona("owner")}>{t("persona.owner")}</OutlineButton>
            </div>
          )}

          {false && !!personaEcho && (
            <ChatBubble role="user" delayed={100}>
              <p>{personaEcho}</p>
            </ChatBubble>
          )}

          {false && step >= 1 && (
            <>
              <ChatBubble role="system" delayed={150}>
                <TypeText text={t("renterPrompt")} />
                <div className="stake-ai-checkboxes">
                  <label>
                    <input type="checkbox" checked={reasonsIncludes("find-home")} onChange={() => toggleReason("find-home")} />
                    <span>{t("reasons.findHome")}</span>
                  </label>
                  <label>
                    <input type="checkbox" checked={reasonsIncludes("cashback")} onChange={() => toggleReason("cashback")} />
                    <span>{t("reasons.cashback")}</span>
                  </label>
                  <label>
                    <input type="checkbox" checked={reasonsIncludes("credit")} onChange={() => toggleReason("credit")} />
                    <span>{t("reasons.credit")}</span>
                  </label>
                  <label>
                    <input type="checkbox" checked={reasonsIncludes("other")} onChange={() => toggleReason("other")} />
                    <span>{t("reasons.other")}</span>
                  </label>
                  {reasonsIncludes("other") && (
                    <input
                      className="stake-ai-input"
                      placeholder={t("methods.pleaseSpecify")}
                      value={otherReason}
                      onChange={(e) => setOtherReason(e.target.value)}
                    />
                  )}
                </div>
              </ChatBubble>
              {!!reasonEcho && (
                <ChatBubble role="user" delayed={120}>
                  <p>
                    {[...reasonsWithOther()]
                      .map((keyOrOther) => {
                        if (typeof keyOrOther === "string" && keyOrOther.startsWith("other:")) {
                          return `${t("reasons.other")}: ${keyOrOther.split(":").slice(1).join(":")}`;
                        }
                        return getReasonLabel(keyOrOther);
                      })
                      .join(", ")}
                  </p>
                </ChatBubble>
              )}
              {showGoogleCTA && (
                <ChatBubble role="system" delayed={150}>
                  <TypeText text={getTailoredIntro(renterReasons)} />
                  <div className="google-signin">
                    <OutlineButton className="button-with-icon" onClick={beginGoogleSignIn}>
                      <img className="google-icon" src="/google-logo.svg" alt="" />
                      {t("brand.googleCta")}
                    </OutlineButton>
                    <button className="as-link" onClick={() => setManualMode(true)}>{t("brand.enterManually")}</button>
                  </div>
                </ChatBubble>
              )}
              {manualMode && (
                <ChatBubble role="system" delayed={100}>
                  <div className="stake-ai-form fade-in visible">
                    <div className="grid two">
                      <Field label={t("manual.name")}>
                        <input className="stake-ai-input" value={manualName} onChange={(e) => setManualName(e.target.value)} />
                      </Field>
                      <Field label={t("manual.email")}>
                        <input className="stake-ai-input" value={manualEmail} onChange={(e) => setManualEmail(e.target.value)} />
                      </Field>
                    </div>
                    <Field label={t("manual.phone")}>
                      <input className="stake-ai-input" value={manualPhone} onChange={(e) => setManualPhone(e.target.value)} />
                    </Field>
                    <div className="stake-ai-actions">
                      <PrimaryButton onClick={submitManualInfo}>{t("manual.continue")}</PrimaryButton>
                    </div>
                  </div>
                </ChatBubble>
              )}
            </>
          )}

          {false && step === 4 && (
            <>
              <ChatBubble role="system" delayed={100}>
                <TypeText text={t("cashback.thanks", { firstName: getFirstName() || "", punct: getFirstName() ? "." : "" })} />
              </ChatBubble>

              {!!rentPayMethod && (
                <ChatBubble role="user" delayed={120}>
                  <p>
                    {rentPayMethod === "other"
                      ? `Other${rentPayOther ? `: ${rentPayOther}` : ""}`
                      : rentPayMethod === "property-portal"
                      ? "Property's Portal"
                      : rentPayMethod.toUpperCase()}
                  </p>
                </ChatBubble>
              )}

              <div className="stake-ai-options">
                <OutlineButton
                  onClick={() => {
                    setRentPayMethod("zelle");
                    save({ rentPayMethod: "zelle" });
                  }}
                >
                  {t("methods.zelle")}
                </OutlineButton>
                <OutlineButton
                  onClick={() => {
                    setRentPayMethod("venmo");
                    save({ rentPayMethod: "venmo" });
                  }}
                >
                  {t("methods.venmo")}
                </OutlineButton>
                <OutlineButton
                  onClick={() => {
                    setRentPayMethod("property-portal");
                    save({ rentPayMethod: "property-portal" });
                  }}
                >
                  {t("methods.portal")}
                </OutlineButton>
                <OutlineButton
                  onClick={() => {
                    setRentPayMethod("ach");
                    save({ rentPayMethod: "ach" });
                  }}
                >
                  {t("methods.ach")}
                </OutlineButton>
                <OutlineButton
                  onClick={() => {
                    setRentPayMethod("other");
                  }}
                >
                  {t("methods.other")}
                </OutlineButton>
              </div>
              {rentPayMethod === "other" && (
                <div className="stake-ai-form fade-in visible">
                  <Field label={t("methods.pleaseSpecify")}>
                    <input
                      className="stake-ai-input"
                      value={rentPayOther}
                      onChange={(e) => {
                        setRentPayOther(e.target.value);
                        save({ rentPayMethod: "other", rentPayOther: e.target.value });
                      }}
                    />
                  </Field>
                </div>
              )}
            </>
          )}

          {false && step === 3 && (
            <>
              <ChatBubble role="system" delayed={100}>
                <TypeText text={t("credit.thanks", { firstName: getFirstName() || "", punctComma: getFirstName() ? "," : "" })} />
              </ChatBubble>

              {!!rentPayMethod && (
                <ChatBubble role="user" delayed={120}>
                  <p>
                    {rentPayMethod === "other"
                      ? `Other${rentPayOther ? `: ${rentPayOther}` : ""}`
                      : rentPayMethod === "property-portal"
                      ? "Property's Portal"
                      : rentPayMethod.toUpperCase()}
                  </p>
                </ChatBubble>
              )}

              <div className="stake-ai-options">
                <OutlineButton
                  onClick={() => {
                    setRentPayMethod("zelle");
                    save({ rentPayMethod: "zelle" });
                  }}
                >
                  {t("methods.zelle")}
                </OutlineButton>
                <OutlineButton
                  onClick={() => {
                    setRentPayMethod("venmo");
                    save({ rentPayMethod: "venmo" });
                  }}
                >
                  {t("methods.venmo")}
                </OutlineButton>
                <OutlineButton
                  onClick={() => {
                    setRentPayMethod("property-portal");
                    save({ rentPayMethod: "property-portal" });
                  }}
                >
                  {t("methods.portal")}
                </OutlineButton>
                <OutlineButton
                  onClick={() => {
                    setRentPayMethod("ach");
                    save({ rentPayMethod: "ach" });
                  }}
                >
                  {t("methods.ach")}
                </OutlineButton>
                <OutlineButton
                  onClick={() => {
                    setRentPayMethod("other");
                  }}
                >
                  {t("methods.other")}
                </OutlineButton>
              </div>
              {rentPayMethod === "other" && (
                <div className="stake-ai-form fade-in visible">
                  <Field label={t("methods.pleaseSpecify")}>
                    <input
                      className="stake-ai-input"
                      value={rentPayOther}
                      onChange={(e) => {
                        setRentPayOther(e.target.value);
                        save({ rentPayMethod: "other", rentPayOther: e.target.value });
                      }}
                    />
                  </Field>
                </div>
              )}
            </>
          )}

          {false && step === 2 && renterReasons.includes("find-home") && (
            <>
              <ChatBubble role="system" delayed={100}>
                <TypeText text={t("findHome.intro")} />
              </ChatBubble>

              <div className="stake-ai-form fade-in visible">
                <h3 className="form-section">Contact Information</h3>
                <div className="grid two">
                  <Field label="First Name">
                    <input className="stake-ai-input" value={form.firstName} onChange={(e) => updateForm("firstName", e.target.value)} />
                  </Field>
                  <Field label="Last Name">
                    <input className="stake-ai-input" value={form.lastName} onChange={(e) => updateForm("lastName", e.target.value)} />
                  </Field>
                </div>
                <div className="grid two">
                  <Field label="Phone">
                    <input className="stake-ai-input" value={form.phone} onChange={(e) => updateForm("phone", e.target.value)} />
                  </Field>
                  <Field label="Email">
                    <input className="stake-ai-input" value={form.email} onChange={(e) => updateForm("email", e.target.value)} />
                  </Field>
                </div>
                <Field label="Where did you hear about us?">
                  <input className="stake-ai-input" value={form.hearAboutUs} onChange={(e) => updateForm("hearAboutUs", e.target.value)} />
                </Field>

                <h3 className="form-section">Preferences</h3>
                <div className="grid two">
                  <Field label="Select Market">
                    <input className="stake-ai-input" value={form.market} onChange={(e) => updateForm("market", e.target.value)} />
                  </Field>
                  <Field label="Neighborhoods">
                    <input className="stake-ai-input" value={form.neighborhoods} onChange={(e) => updateForm("neighborhoods", e.target.value)} />
                  </Field>
                </div>
                <div className="grid two">
                  <Field label="Neighborhood">
                    <input className="stake-ai-input" value={form.neighborhood} onChange={(e) => updateForm("neighborhood", e.target.value)} />
                  </Field>
                  <Field label="Move-in Date">
                    <input type="date" className="stake-ai-input" value={form.moveInDate} onChange={(e) => updateForm("moveInDate", e.target.value)} />
                  </Field>
                </div>
                <div className="grid three">
                  <Field label="Lease Term (months)">
                    <input className="stake-ai-input" value={form.leaseTermMonths} onChange={(e) => updateForm("leaseTermMonths", e.target.value)} />
                  </Field>
                  <Field label="Number of Pets">
                    <input className="stake-ai-input" value={form.petsCount} onChange={(e) => updateForm("petsCount", e.target.value)} />
                  </Field>
                  <Field label="Pet Type">
                    <input className="stake-ai-input" value={form.petType} onChange={(e) => updateForm("petType", e.target.value)} />
                  </Field>
                </div>
                <div className="grid three">
                  <Field label="Number of Parking">
                    <input className="stake-ai-input" value={form.parkingCount} onChange={(e) => updateForm("parkingCount", e.target.value)} />
                  </Field>
                  <Field label="Where are you open to parking?">
                    <input className="stake-ai-input" value={form.parkingPreference} onChange={(e) => updateForm("parkingPreference", e.target.value)} />
                  </Field>
                  <Field label="Square Feet">
                    <input className="stake-ai-input" value={form.sqft} onChange={(e) => updateForm("sqft", e.target.value)} />
                  </Field>
                </div>
                <div className="grid three">
                  <Field label="Price">
                    <input className="stake-ai-input" value={form.price} onChange={(e) => updateForm("price", e.target.value)} />
                  </Field>
                  <Field label="Bedroom">
                    <input className="stake-ai-input" value={form.bedrooms} onChange={(e) => updateForm("bedrooms", e.target.value)} placeholder="Studio / 1 / 2 / 3 / 4 / Convertible" />
                  </Field>
                  <Field label="Bathroom">
                    <input className="stake-ai-input" value={form.bathrooms} onChange={(e) => updateForm("bathrooms", e.target.value)} placeholder="1+ / 1.5+ / 2+ / 2.5+ / 3+" />
                  </Field>
                </div>

                <Field label="Notes">
                  <textarea className="stake-ai-input" rows={4} value={form.notes} onChange={(e) => updateForm("notes", e.target.value)} />
                </Field>

                <h3 className="form-section">Unit Features & Building Amenities</h3>
                <div className="grid two">
                  {[
                    ["attachedPrivateGarages", "Attached Private Garages"],
                    ["dogRun", "Dog Run"],
                    ["evCharging", "EV Charging Stations"],
                    ["elevators", "Elevators"],
                    ["fencedCommunity", "Fenced Community"],
                    ["fencedYardsTx", "Fenced Yards"],
                    ["petWash", "Pet Wash"],
                    ["rooftopDeck", "Rooftop Deck"],
                    ["wdConnections", "W/D Connections"],
                    ["wdIncluded", "W/D Included"],
                  ].map(([key, label]) => (
                    <label key={key} className="checkbox">
                      <input type="checkbox" checked={!!form.amenities[key]} onChange={(e) => updateNested("amenities", key, e.target.checked)} />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>

                <h3 className="form-section">Credit Score & Background Verification</h3>
                <Field label="Credit Score">
                  <select className="stake-ai-input" value={form.creditScore} onChange={(e) => updateForm("creditScore", e.target.value)}>
                    <option value="">Select...</option>
                    <option value="no-score">I don't have a credit score</option>
                    <option value="300-499">300-499</option>
                    <option value="500-549">500-549</option>
                    <option value="550-599">550-599</option>
                    <option value="600-649">600-649</option>
                    <option value="650+">650+</option>
                  </select>
                </Field>
                <div className="grid two">
                  {[
                    ["none", "None"],
                    ["rentalDebt", "Active Rental Debt"],
                    ["bankruptcy", "Bankruptcy"],
                    ["eviction", "Eviction"],
                    ["felony", "Felony"],
                    ["misdemeanor", "Misdemeanor"],
                    ["unpaidBrokenLease", "Unpaid Broken Lease"],
                  ].map(([key, label]) => (
                    <label key={key} className="checkbox">
                      <input type="checkbox" checked={!!form.background[key]} onChange={(e) => updateNested("background", key, e.target.checked)} />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>

                <div className="stake-ai-actions">
                  <PrimaryButton onClick={submitFindHome}>{t("manual.continue")}</PrimaryButton>
                </div>
              </div>
            </>
          )}

          {false && step === 3 && (
            <ChatBubble role="system" delayed={50}>
              <TypeText text={t("saved")} />
            </ChatBubble>
          )}
          {false && (step === 100 || step === 200) && showGoogleCTA && (
            <ChatBubble role="system" delayed={120}>
              <TypeText text={t("intro.generic")} />
              <div className="google-signin">
                <OutlineButton className="button-with-icon" onClick={beginGoogleSignIn}>
                  <img className="google-icon" src="/google-logo.svg" alt="" />
                  {t("brand.googleCta")}
                </OutlineButton>
              </div>
            </ChatBubble>
          )}
        </div>
      </div>
    </div>
  );
}


